import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Volume2, VolumeX } from "lucide-react";
import { HERO_VIDEO, IMG } from "../data/content";
import { openEnquiry } from "../components/Header";

export default function Hero() {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.play().catch(() => {});
    setMuted(v.muted);
  };

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] overflow-hidden">
      <h1 className="sr-only">
        The Jamoon — Wilderness Lodge in Corbett
      </h1>
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          className="hero-media-drift h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={IMG.heroPoster}
          aria-label="The Jamoon wilderness lodge and surrounding forest in Corbett"
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal" />
      </div>

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 flex h-full items-end justify-center px-6 pb-40 md:pb-24"
      >
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <button
            data-testid="hero-plan-journey-btn"
            onClick={openEnquiry}
            className="group flex items-center gap-3 border border-brass/60 bg-charcoal/40 px-8 py-4 text-xs font-semibold tracking-[0.28em] text-ivory backdrop-blur-sm transition-colors duration-500 hover:border-brass hover:bg-brass hover:text-charcoal"
          >
            PLAN YOUR JAMOON JOURNEY
            <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1.5" />
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-sound-toggle-btn"
        onClick={toggleSound}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute bottom-[4.5rem] left-4 z-20 flex items-center justify-center gap-2.5 rounded-full border border-ivory/25 bg-charcoal/50 p-3 text-[10px] font-medium tracking-[0.25em] text-ivory/85 backdrop-blur-sm transition-colors duration-300 hover:border-brass hover:text-brass md:bottom-24 md:left-auto md:right-10 md:rounded-none md:px-4 md:py-2.5"
        aria-label={muted ? "Turn sound on" : "Turn sound off"}
        aria-pressed={!muted}
      >
        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        <span className="hidden md:inline">{muted ? "SOUND OFF" : "SOUND ON"}</span>
      </motion.button>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-24 left-1/2 z-10 -translate-x-1/2 md:bottom-8"
        aria-hidden="true"
      >
        <div className="h-14 w-px bg-gradient-to-b from-transparent via-brass/70 to-transparent" />
      </motion.div>
    </section>
  );
}
